import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Truncate from 'react-truncate';
import styled, { css } from 'styled-components';

import { useConfig } from '../../hooks';
import { useStores } from '../../stores';
import { detectBrowser, sanitizeHTML } from '../../utilities';
import Button from '../Button';
import ButtonLink from '../ButtonLink';
import BuildingIcon from '../Icons/Building';
import SalaryIcon from '../Icons/SackDollar';
import HierachyIcon from '../Icons/SiteMap';
import Loading from '../Loading';
import ToolTip from '../ToolTip';
import ListConten from './ListContent';

const CarouselSlide = ({ data, language, buttonTranslation }) => {
  const {
    id,
    apiURL,
    fontFamily,
    button,
    source,
    window: { colors }
  } = useConfig();
  const { app, bot } = useStores();
  const { i18n, t } = useTranslation();
  const { isIE } = detectBrowser;
  const [tooltipText, setTooltipText] = React.useState('');
  const [isLoadedItem, setLoadedItem] = React.useState(false);
  const titleRef = React.useRef(null);

  React.useEffect(() => {
    setLoadedItem(!isEmpty(data.intentData));
  }, [data.intentData]);

  // Display a tolltip or not
  /**
   * need to display only 2 lines (so multiply the line-height by 2)
   * 53 = line-height * 2 + padding-top + padding-bottom + border
   * 53 = 22px        * 2 + 4px         + 4px            + 1px
   */
  const maxTitleHeight = 53;
  React.useEffect(() => {
    if (
      !isEmpty(data.intentData) &&
      titleRef.current &&
      titleRef.current.scrollHeight > maxTitleHeight
    ) {
      setTooltipText(data.intentData.title || titleRef.current.innerText);
    }
  }, [data.intentData]);

  if (data.list && data.list.length > 0) {
    return (
      <Root>
        <Image srcLink={data.imageLink} baseUrl={apiURL} />
        <ContentWrapper>
          <Title textColor={colors.accent} isIE={isIE}>
            {t(data.name)}
          </Title>
          <ListConten data={data.list} isTopFrequentQuestions={data.isTopFrequentQuestions} />
        </ContentWrapper>
      </Root>
    );
  }

  if (data.jobSearchContent) {
    const { industry, seniority, salary, link } = data.jobSearchContent;
    return (
      <Root>
        <ContentWrapper textColor={button.color} textHoverColor={button.hoverColor}>
          <ToolTip
            text={tooltipText}
            position="bottom"
            positionDistance={0}
            width="100%"
            textFontFamily={fontFamily}
          >
            <Title ref={titleRef} textColor={colors.accent} isIE={isIE}>
              {data.name}
            </Title>
          </ToolTip>

          <TextWrapper>
            <TextIcon>
              <BuildingIcon /> <span>{industry}</span>
            </TextIcon>
            <TextIcon>
              <HierachyIcon />
              <span>{seniority}</span>
            </TextIcon>
            <TextIcon>
              <SalaryIcon />
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(salary)
                }}
              />
            </TextIcon>
          </TextWrapper>
          <ButtonLink
            className="carousel-link"
            link={link}
            textFontFamily={fontFamily}
            color={button.color}
            hoverColor={button.hoverColor}
            borderRadius={button.borderRadius}
          >
            {t('viewMore')}
          </ButtonLink>
        </ContentWrapper>
      </Root>
    );
  }

  if (!isEmpty(data.intentData)) {
    return (
      <Root>
        {!isLoadedItem && (
          <LoaderWrapper>
            <Loading text="" color={button.color} size={14} />
          </LoaderWrapper>
        )}
        <Image srcLink={data.imageLink} baseUrl={apiURL} />
        <ContentWrapper textColor={button.color} textHoverColor={button.hoverColor}>
          <ToolTip
            text={tooltipText}
            position="top"
            positionDistance={0}
            width="100%"
            textFontFamily={fontFamily}
          >
            <Title
              ref={titleRef}
              textColor={colors.accent}
              isIE={isIE}
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(data.intentData.title)
              }}
            />
          </ToolTip>

          <TextWrapper>
            {isIE ? (
              <Truncate lines={3} ellipsis="...">
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(data.intentData.text)
                  }}
                />
              </Truncate>
            ) : (
              <Text
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(data.intentData.text)
                }}
              />
            )}
          </TextWrapper>
          {data.urlLink ? (
            <ButtonLink
              className="carousel-link"
              link={data.urlLink}
              textFontFamily={fontFamily}
              color={button.color}
              hoverColor={button.hoverColor}
              borderRadius={button.borderRadius}
            >
              {buttonTranslation}
            </ButtonLink>
          ) : (
            <Button
              textFontFamily={fontFamily}
              color={button.color}
              hoverColor={button.hoverColor}
              borderRadius={button.borderRadius}
              onClick={() => {
                return bot.getMqaLanguageForCarousel({
                  ProjectId: id,
                  MesId: data.intentData.mesId,
                  Source: source,
                  SessionId: app.sessionId,
                  CrossProjectName: '',
                  Question: data.intentData.title,
                  TextLanguage: language || i18n.language
                });
              }}
              disabled={!isLoadedItem}
            >
              {buttonTranslation}
            </Button>
          )}
        </ContentWrapper>
      </Root>
    );
  }

  return null;
};

CarouselSlide.propTypes = {
  data: PropTypes.shape({
    imageLink: PropTypes.string,
    name: PropTypes.string,
    list: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        mesId: PropTypes.number
      })
    ),
    mesId: PropTypes.number,
    intentData: PropTypes.shape({
      title: PropTypes.string,
      mesId: PropTypes.number,
      text: PropTypes.string
    }),
    urlLink: PropTypes.string,
    jobSearchContent: PropTypes.shape({
      industry: PropTypes.string,
      seniority: PropTypes.string,
      salary: PropTypes.string,
      link: PropTypes.string
    }),
    isTopFrequentQuestions: PropTypes.bool
  }).isRequired,
  language: PropTypes.string,
  buttonTranslation: PropTypes.string
};

CarouselSlide.defaultProps = {
  language: '',
  buttonTranslation: 'View More'
};

const Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: #fff;
  border-radius: 18px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const LoaderWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: #fff;
`;

const Image = styled.div`
  display: block;
  width: 100%;
  height: 120px;
  background-color: transparent;
  background-image: ${props =>
    props.srcLink
      ? `url(${props.srcLink})`
      : `url(${props.baseUrl}/cfp2/images/carouseldefault.png)`};
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  -webkit-print-color-adjust: exact;
  color-adjust: exact;
`;

const ContentWrapper = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  padding: 0px 16px 16px;

  span {
    text-align: left;
  }
  a.carousel-link,
  a.carousel-link:link {
    color: ${props => props.textColor} !important;
  }
  a.carousel-link:hover,
  a.carousel-link:focus {
    color: ${props => props.textHoverColor} !important;
  }
`;

const Title = styled.h4`
  display: -webkit-box;
  width: 188px;
  padding: 4px 0;
  margin: 0 0 8px;
  font-size: 14px !important;
  font-weight: 700;
  line-height: 22px;
  text-align: left;
  color: ${props => props.textColor};
  border-bottom: 1px solid #e3e3e3 !important;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  /* for IE */
  ${({ isIE }) =>
    isIE &&
    css`
      display: block;
      white-space: nowrap;
      text-overflow: ellipsis;
    `}
`;

const TextWrapper = styled.div`
  flex: 1 0 auto;
  margin-bottom: 8px;
`;

const Text = styled.p`
  display: -webkit-box;
  max-height: 54px;
  font-size: 12px !important;
  line-height: 1.5 !important;
  text-align: left;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  * {
    font-size: 12px !important;
  }
`;

const TextIcon = styled.p`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  :not(:last-of-type) {
    margin-bottom: 6px;
  }
  svg {
    position: relative;
    top: 4px;
    margin-right: 8px;
    font-size: 12px;
  }
`;

export default CarouselSlide;
